<template>
  <div class="proofreading-select-checks">
    <h1 class="proofreading-select-checks__heading">
      {{ $t("proofreadingSelectChecksPage.heading") }}
    </h1>

    <div class="proofreading-select-checks__list" role="radiogroup">
      <div
        v-for="(checkId, index) in checkIds"
        :key="checkId"
        class="check-row"
        :class="[rowSurfaceClass(index), { 'check-row--disabled': !isCheckSelectable(checkId) }]"
        role="radio"
        :aria-checked="isCheckEnabled(checkId)"
        :aria-disabled="!isCheckSelectable(checkId)"
        @click="onSelectCheck(checkId)"
      >
        <span class="check-row__radio" :data-disabled="!isCheckSelectable(checkId)">
          <RadioIcon
            :value="checkId"
            :selected="selectedCheckId"
            :fill="checkboxFill(isCheckEnabled(checkId))"
          />
        </span>
        <span class="check-row__label">{{ $t(checkMeta(checkId).labelKey) }}</span>
        <button
          type="button"
          class="check-row__info-button"
          :aria-label="
            $t('proofreadingItemSelectDialog.showRuleDescription', {
              label: $t(checkMeta(checkId).labelKey),
            })
          "
          :disabled="isSubmitting"
          @click.stop="onClickRuleInfo(checkId)"
        >
          <IconInfo />
        </button>
      </div>
    </div>

    <div v-if="checkWarnings.length > 0" class="proofreading-select-checks__warnings">
      <p
        v-for="warning in checkWarnings"
        :key="warning.id"
        class="check-warning"
      >
        <span class="check-warning__text">{{ $t(warning.messageKey) }}</span>
        <router-link
          v-if="warning.action === 'theme' && novelId"
          :to="{ name: 'theme', params: { novelId } }"
          class="check-warning__link"
        >
          {{ $t("proofreadingSelectChecksPage.themeLink") }}
        </router-link>
      </p>
    </div>

    <div class="proofreading-select-checks__estimate">
      <p class="estimate-main">
        <i18n
          v-if="enabledCheckCount > 0"
          path="proofreadingSelectChecksPage.estimateWithBreakdown"
          tag="span"
        >
          <strong place="amount" class="estimate-amount">{{ formattedEstimatedFish }} fish</strong>
          <span place="chars">{{ formattedTotalChars }}</span>
          <span place="rate">{{ fishPerChar }}</span>
        </i18n>
        <i18n v-else path="proofreadingSelectChecksPage.estimate" tag="span">
          <strong place="amount" class="estimate-amount">{{ formattedEstimatedFish }} fish</strong>
        </i18n>
      </p>
      <div v-if="isFishInsufficient" class="fish-warning">
        <p class="fish-warning__message">{{ $t("proofreadingSelectChecksPage.fishInsufficient") }}</p>
      </div>
    </div>

    <footer class="proofreading-select-checks__footer">
      <button
        type="button"
        class="proofreading-select-checks__back-button"
        :disabled="isSubmitting"
        @click="onClickBack"
      >
        {{ $t("proofreadingSelectChecksPage.back") }}
      </button>
      <button
        type="button"
        class="proofreading-select-checks__run-button"
        :disabled="!canProceed"
        @click="onClickNext"
      >
        {{
          isSubmitting
            ? $t("proofreadingSelectChecksPage.submitting")
            : $t("proofreadingSelectChecksPage.runProofreading")
        }}
      </button>
    </footer>

    <div class="proofreading-select-checks__notice">
      <p class="notice-text">{{ $t("proofreadingSelectChecksPage.noticeIssueLimit") }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import RadioIcon from "@/components/atoms/RadioIcon.vue";
import ProofreadingAiConfirmDialog, {
  ProofreadingAiConfirmDialogResult,
} from "@/components/ui/dialogs/ProofreadingAiConfirmDialog.vue";
import ProofreadingRuleDescriptionDialog from "@/components/ui/dialogs/ProofreadingRuleDescriptionDialog.vue";
import { showNotifyDialog } from "@/lib/dialog";
import { Dialog } from "@/lib/utils/dialog";
import {
  postExtractProofreading,
  type ProofreadingBatchCheckOptions,
} from "@/lib/api/proofreadingAi";
import {
  AI_PROOFREADING_CHECK_IDS,
  AI_PROOFREADING_CHECK_I18N,
  AI_PROOFREADING_FISH_PER_CHAR,
  isAiProofreadingCheckSelectable,
  type AiProofreadingCheckId,
} from "@/constants/proofreadingAiChecks";
import { THEME_FIELD_NAMES } from "@/constants/themeReview";
import { useProofreadingAiStore } from "@/stores/proofreadingAi";
import { useThemeStore } from "@/stores/theme";
import { ProofreadingAnalytics, toProofreadingMethod } from "@/lib/utils/analytics/proofreading";
import { sanitizeAiChecksForSubmit } from "@/lib/utils/proofreadingAiFish";
import { handleRuleBased402Error, handleProofreadingNewRun409Error } from "@/lib/utils/proofreadingApiError";
import IconInfo from "~icons/figma/Info";

type CheckWarning = {
  id: AiProofreadingCheckId;
  messageKey: string;
  action?: "theme";
};

export default Vue.extend({
  components: {
    RadioIcon,
    IconInfo,
  },
  props: {
    novelId: String,
  },
  data() {
    return {
      proofreadingAiStore: useProofreadingAiStore(),
      isSubmitting: false,
      analytics: null as ProofreadingAnalytics | null,
      checkIds: AI_PROOFREADING_CHECK_IDS,
    };
  },
  computed: {
    enabledCheckCount(): number {
      return this.proofreadingAiStore.enabledCheckCount;
    },
    fishPerChar(): number {
      return AI_PROOFREADING_FISH_PER_CHAR;
    },
    selectedCheckId(): AiProofreadingCheckId | "" {
      return this.checkIds.find((checkId) => this.isCheckEnabled(checkId)) ?? "";
    },
    formattedTotalChars(): string {
      return this.proofreadingAiStore.totalChars.toLocaleString(this.$i18n.locale || "ja");
    },
    formattedEstimatedFish(): string {
      return this.proofreadingAiStore.estimatedFish.toLocaleString(this.$i18n.locale || "ja");
    },
    currentFish(): number {
      return this.$store.getters["fishModule/currentFish"] as number;
    },
    isFishInsufficient(): boolean {
      const fish = this.proofreadingAiStore.estimatedFish;
      if (fish <= 0) return false;
      const canConsume = this.$store.getters["fishModule/canConsumeFish"];
      if (typeof canConsume === "function") {
        return !canConsume(fish);
      }
      return this.currentFish < fish;
    },
    hasEnabledCheck(): boolean {
      return this.enabledCheckCount > 0;
    },
    canProceed(): boolean {
      return (
        !this.isSubmitting
        && this.hasEnabledCheck
        && this.proofreadingAiStore.selectedManuscriptKeys.length > 0
        && this.proofreadingAiStore.totalChars > 0
        && !this.isFishInsufficient
      );
    },
    hasThemeContent(): boolean {
      const themeStore = useThemeStore();
      const details = themeStore.theme?.detail ?? [];
      return THEME_FIELD_NAMES.some((name) => {
        const value = details.find((detail) => detail.name === name)?.value?.trim();
        return !!value;
      });
    },
    checkWarnings(): CheckWarning[] {
      const warnings: CheckWarning[] = [];
      if (this.isCheckEnabled("storyCheck") && !this.hasThemeContent) {
        warnings.push({
          id: "storyCheck",
          messageKey: AI_PROOFREADING_CHECK_I18N.storyCheck.warningKey!,
          action: "theme",
        });
      }
      if (this.isCheckEnabled("foreshadowCheck") && !this.hasThemeContent) {
        warnings.push({
          id: "foreshadowCheck",
          messageKey: AI_PROOFREADING_CHECK_I18N.foreshadowCheck.warningKey!,
          action: "theme",
        });
      }
      return warnings;
    },
  },
  async mounted() {
    this.analytics = new ProofreadingAnalytics(this.$gtm);
    if (this.novelId) {
      this.proofreadingAiStore.setActiveNovelId(this.novelId);
      this.proofreadingAiStore.setMode("ai");
      this.$store.dispatch("fishModule/fetchFishData");
      const themeStore = useThemeStore();
      await themeStore.fetchTheme(this.novelId);
    }

    if (this.proofreadingAiStore.selectedManuscriptKeys.length === 0 && this.novelId) {
      this.$router.replace({
        name: "proofreadingSelectManuscript",
        params: { novelId: this.novelId },
        query: { mode: "ai" },
      });
    }
  },
  methods: {
    checkMeta(checkId: AiProofreadingCheckId) {
      return AI_PROOFREADING_CHECK_I18N[checkId];
    },
    onClickBack() {
      if (!this.novelId || this.isSubmitting) return;
      this.$router.push({
        name: "proofreadingSelectManuscript",
        params: { novelId: this.novelId },
        query: { mode: "ai" },
      });
    },
    isCheckEnabled(checkId: AiProofreadingCheckId): boolean {
      return this.proofreadingAiStore.selectedChecks[checkId];
    },
    isCheckSelectable(checkId: AiProofreadingCheckId): boolean {
      return isAiProofreadingCheckSelectable(checkId);
    },
    checkboxFill(checked: boolean): string {
      return checked ? "var(--color-primary)" : "var(--color-inactive)";
    },
    rowSurfaceClass(index: number): string {
      return index % 2 === 0 ? "check-row--surface-a" : "check-row--surface-b";
    },
    onSelectCheck(checkId: AiProofreadingCheckId) {
      if (this.isSubmitting || !this.isCheckSelectable(checkId) || this.isCheckEnabled(checkId)) return;
      const next: ProofreadingBatchCheckOptions = {
        errorCheck: false,
        nounCheck: false,
        storyCheck: false,
        expressionCheck: false,
        foreshadowCheck: false,
        [checkId]: true,
      };
      this.proofreadingAiStore.setSelectedChecks(next);
    },
    async onClickRuleInfo(checkId: AiProofreadingCheckId) {
      const meta = this.checkMeta(checkId);
      await new Dialog(ProofreadingRuleDescriptionDialog).show({
        label: this.$t(meta.labelKey) as string,
        description: this.$t(meta.descriptionKey) as string,
      });
    },
    async onClickNext() {
      if (!this.canProceed || !this.novelId || this.isSubmitting) return;

      const confirmed = await new Dialog(ProofreadingAiConfirmDialog).show({
        estimatedFish: this.proofreadingAiStore.estimatedFish,
        isSubmitting: false,
      }) as ProofreadingAiConfirmDialogResult;

      if (!confirmed) return;

      this.isSubmitting = true;
      try {
        const manuscripts = this.proofreadingAiStore.selectedManuscriptKeys.map((key) => ({
          manuscriptKey: key,
        }));

        const checks = sanitizeAiChecksForSubmit(this.proofreadingAiStore.selectedChecks);
        const response = await postExtractProofreading({
          novelId: this.novelId,
          manuscripts,
          checks,
        });

        this.proofreadingAiStore.setEstimatedFish(response.estimatedFish);
        this.proofreadingAiStore.rememberBatchId(this.novelId, response.batchId);
        this.proofreadingAiStore.setBatchStatus("RUNNING");
        await this.$store.dispatch("fishModule/fetchFishData");

        this.analytics?.trackAiSubmit({
          novelId: this.novelId,
          totalChars: this.proofreadingAiStore.totalChars,
          checkCount: this.proofreadingAiStore.enabledCheckCount,
          estimatedFish: response.estimatedFish,
          batchId: response.batchId,
          method: toProofreadingMethod(checks),
        });

        this.$router.push({
          name: "proofreadingWaiting",
          params: { novelId: this.novelId },
        });
      } catch (error) {
        const handled409 = await handleProofreadingNewRun409Error(error, this.novelId);
        if (handled409) return;

        const handled402 = await handleRuleBased402Error(error, this.novelId);
        if (handled402) return;

        const message = error instanceof Error ? error.message : String(error);
        await showNotifyDialog({
          content: message,
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.proofreading-select-checks {
  display: flex;
  flex-direction: column;
  max-width: 640px;
  margin: 0 auto;
  padding: 40px 24px;
  color: var(--color-text-normal);
  background-color: var(--color-background-normal);
}

.proofreading-select-checks__heading {
  margin: 0 0 16px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  text-align: left;
  color: var(--color-text-normal);
}

.proofreading-select-checks__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.check-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 24px;
  padding: 12px 16px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  user-select: none;
}

.check-row--surface-a {
  background-color: var(--color-background-normal);
}

.check-row--surface-b {
  background-color: var(--color-surface-base);
}

.check-row--disabled {
  cursor: not-allowed;
}

.check-row__radio {
  display: inline-flex;
  flex-shrink: 0;
  line-height: 0;

  &[data-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.check-row__label {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.check-row--disabled .check-row__label {
  color: var(--color-inactive);
}

.check-row__info-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: var(--radius-rounded);
  background: transparent;
  color: var(--color-text-low);
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.proofreading-select-checks__warnings {
  margin-top: 12px;
}

.check-warning {
  margin: 0 0 8px;
  font-size: var(--font-size-xs);
  color: var(--color-warning-text);
  line-height: var(--line-height-default);
}

.check-warning__text {
  margin-right: 8px;
}

.check-warning__link {
  border: none;
  padding: 0;
  background: transparent;
  font-size: var(--font-size-xs);
  color: var(--color-primary);
  cursor: pointer;
  text-decoration: underline;
}

.proofreading-select-checks__estimate {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin: 16px 0 0;
  padding: 0 4px;
  text-align: right;
}

.estimate-main {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

.estimate-amount {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}

.fish-warning {
  align-self: stretch;
  margin-top: 12px;
  padding: 12px;
  border-radius: var(--radius-xs);
  background-color: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border);
  text-align: left;
}

.fish-warning__message {
  margin: 0;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-warning-text);
}

.proofreading-select-checks__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 0 4px;
}

.proofreading-select-checks__back-button {
  border: none;
  border-radius: var(--radius-rounded);
  padding: 12px 32px;
  background-color: #ebebeb;
  color: var(--color-gray-1);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.proofreading-select-checks__run-button {
  margin-left: auto;
  border: none;
  border-radius: var(--radius-rounded);
  padding: 12px 48px;
  background-color: var(--color-primary);
  color: var(--color-text-invert);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.proofreading-select-checks__notice {
  margin-top: 36px;
  padding: 0 4px;
}

.notice-text {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: 1.6;
  color: var(--color-text-normal);
}

@include mq("sp") {
  .proofreading-select-checks {
    padding: 24px 16px;
  }
}

::v-deep .proper-noun-modal--over-proofreading {
  z-index: 9200;
}
</style>
